import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Grid, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import MediaSearchBox from './MediaSearchBox';
import LoadButton from './LoadButton';
import useAPI from '../../../../api';
import { useDebounceState } from '../../../../Hooks/useDebounce';
import { openSnackbarAdviseAtom, selectedBranchAtom } from '../../../../Atoms';
import getErrorMessage from '../../../../Helpers/Errors/getErrorMessage';
import MediaBreadcrumbs from './MediaBreadcrumbs';
import MediaItem from './MediaItem';

const styles = {
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    maxWidth: 'unset',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '12%',
    width: '100%',
    padding: '36px',
  },
};

const MediaWidget = function MediaWidget({ handleSelectImg }) {
  const API = useAPI();
  const intl = useIntl();

  const { id: branchId } = useAtomValue(selectedBranchAtom) || {};
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);

  const [allBranches, setAllBranches] = useState([]);
  const [collectionOpen, setCollectionOpen] = useState(null);
  const [images, setImages] = useState([]);
  const [searchValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useDebounceState(
    searchValue,
    600
  );

  const onChangeSearchValue = (event) => {
    setInputValue(event.target.value);
    setDebouncedValue(event.target.value);
  };

  const handleOpenCollection = (imgId) => {
    setCollectionOpen(imgId);
  };

  const handleCloseCollection = () => {
    setCollectionOpen(null);
  };

  useEffect(() => {
    API.getAllBranches(
      (res) => {
        setAllBranches(res);
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    API.getMedia(
      (res) => {
        setImages(res);
      },
      () => {
        setImages([]);
      }
    );
  }, []);

  const handleUploadImage = (event) => {
    const formData = new FormData();
    [...event.target.files].forEach((file) => formData.append('files', file));

    API.uploadMedia(
      formData,
      { doNotStringify: true },
      (res) => {
        const newImages = res.filter(({ error }) => !error);
        setImages((old) => [...old, ...newImages]);
        openSnackbar({
          severity: 'success',
          message: `${newImages.length} ${intl.messages.MEDIA_UPLOADED_SUCCESSFULLY}`,
        });
      },
      (err) => {
        openSnackbar({
          severity: 'error',
          message: `${intl.messages.ERROR}: ${getErrorMessage(err) || ''}`,
        });
      }
    );
  };

  const removeImg = (imgId, isCollection) => () => {
    API.deleteMedia(
      imgId,
      isCollection,
      { silent: true },
      () => {
        const copyFiles = [...images];
        const index = copyFiles.findIndex(({ id }) => id === imgId);
        copyFiles.splice(index, 1);
        setImages(copyFiles);
        openSnackbar({
          severity: 'success',
          message: intl.messages.MEDIA_DELETED_SUCCESSFULLY,
        });
      },
      () => {
        openSnackbar({
          severity: 'error',
          message: intl.messages.ERROR_DELETING_MEDIA,
        });
      }
    );
  };

  const filteredImages = collectionOpen
    ? allBranches
        .map(({ id, branchDescription }) => ({
          id: collectionOpen?.id,
          branchId: id,
          name: branchDescription,
        }))
        .filter((img) => !debouncedValue || img.name.includes(debouncedValue))
    : images.filter(
        (img) => !debouncedValue || img.name.includes(debouncedValue)
      );

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <MediaSearchBox value={searchValue} onChange={onChangeSearchValue} />
        <LoadButton onLoad={handleUploadImage} accept={'image/*'} multiple />
      </div>
      <MediaBreadcrumbs
        collectionName={collectionOpen?.name}
        closeCollection={handleCloseCollection}
      />
      <Grid
        container
        spacing={4}
        sx={{
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          p: '36px',
        }}
      >
        {filteredImages.length < 1 ? (
          <Typography variant="body1" sx={{
            p: "16px"
          }}>
            {intl.messages.NO_ITEMS}
          </Typography>
        ) : (
          filteredImages
            .sort((a, b) => {
              //collection first

              return a?.isCollection === b?.isCollection
                ? 0
                : a?.isCollection
                  ? -1
                  : 1;
            })
            .map((img) => (
              <Grid
                key={`${img?.id}_${img?.branchId}`}
                item
                size={{ xl: 2, lg: 2, md: 3, sm: 4, xs: 4 }}
                sx={{
                  overflow: 'hidden',
                  aspectRatio: 1,
                }}
              >
                <MediaItem
                  id={img?.id}
                  name={img?.name}
                  isCollection={!handleSelectImg && img?.isCollection}
                  branchId={img?.branchId || branchId}
                  handleRemove={
                    !img?.branchId &&
                    removeImg(
                      img?.id,
                      img?.isCollection
                    ) /*If branchId is true is inside a collection (img delete not allowed)*/
                  }
                  handleOpen={() => handleOpenCollection(img)}
                  handleSelect={
                    handleSelectImg ? handleSelectImg(img?.id) : undefined
                  }
                />
              </Grid>
            ))
        )}
      </Grid>
    </div>
  );
};

MediaWidget.propTypes = {
  handleSelectImg: PropTypes.func,
};
export default MediaWidget;
